import type { Brick, BrickType, BrickColor } from '@models/brick'

/**
 * BrickGrouping Utilities
 *
 * Helper functions for grouping bricks by type and color for instanced rendering.
 * Used by the BrickInstances component to reduce draw calls.
 *
 * Task 4.1.1 - Geometry instancing for identical bricks
 */

/**
 * Generate a unique key for a brick type+color combination
 * Format: "type-r-g-b" (e.g., "2x4-220-20-60")
 */
export function getBrickGroupKey(type: BrickType, color: BrickColor): string {
  return `${type}-${color.r}-${color.g}-${color.b}`
}

/**
 * Group of bricks that share the same type and color
 * Can be rendered as a single instanced mesh
 */
export interface BrickGroup {
  key: string // Unique identifier for this group
  type: BrickType
  color: BrickColor
  bricks: Brick[] // All bricks in this group
}

/**
 * Group bricks by type and color for instanced rendering
 *
 * @param bricks - Map of all bricks to group
 * @returns Array of brick groups, each representing a unique type+color combination
 *
 * @example
 * const groups = groupBricksForInstancing(brickStore.bricks)
 * // [
 * //   { key: "1x2-220-20-60", type: "1x2", color: {r:220,g:20,b:60}, bricks: [brick1, brick2, ...] },
 * //   { key: "2x4-0-120-220", type: "2x4", color: {r:0,g:120,b:220}, bricks: [brick3, ...] },
 * // ]
 */
export function groupBricksForInstancing(bricks: Map<string, Brick>): BrickGroup[] {
  // Create a map of groups by key
  const groupMap = new Map<string, BrickGroup>()

  // Iterate through all bricks and group them
  for (const brick of bricks.values()) {
    const key = getBrickGroupKey(brick.type, brick.color)

    // Get or create group for this key
    let group = groupMap.get(key)
    if (!group) {
      group = {
        key,
        type: brick.type,
        color: brick.color,
        bricks: [],
      }
      groupMap.set(key, group)
    }

    // Add brick to group
    group.bricks.push(brick)
  }

  // Convert map to array and sort by group size (largest first for better batching)
  return Array.from(groupMap.values()).sort((a, b) => b.bricks.length - a.bricks.length)
}

/**
 * Check if two colors are equal
 */
export function colorsEqual(a: BrickColor, b: BrickColor): boolean {
  return a.r === b.r && a.g === b.g && a.b === b.b
}
